import React from 'react';
import ModernSelect from 'src/components/ModernSelect/ModernSelect';
import Col from '../../components/Col';
import Row from '../../components/Row';
import ReportingTable from '../../components/ReportingTable/ReportingTable';
import Button from '../../components/Button';
import ReportingUserActivityTable from '../../components/ReportingTable/ReportingUserActivityTable';
import UserStatusReports from '../../components/ReportingTable/UserStatusReports';
import CustomerFeedbackReportTable from '../../components/ReportingTable/CustomerFeedbackTable';
import { ServiceRequestStatsForm } from './Forms/ServiceRequestStats';
import { ServiceRequestStatsReport } from './Forms/ServiceRequestStats/Report.jsx';
import { ServiceRequestStatsReportSidebar } from './Forms/ServiceRequestStats/ReportSidebar.jsx';
import { OccupancyDeficiencyForm } from 'src/pages/Reporting/Forms/OccupancyDeficiencyForm/OccupancyDeficiencyForm';
import {
  IUseReportingPageOrchestratorReturn,
  useReportingPageOrchestrator,
} from 'src/pages/Reporting/CustomHooks/useReportingPageOrchestrator';
import CustomPagination from 'src/components/CustomPagination/CustomPagination';
import OccupancyDeficiencyTable from 'src/components/ReportingTable/OccupanyDeficiencyTable/OccupancyDeficiencyTable';
import PropertyAggregateTable from 'src/components/ReportingTable/PropertyAggregateTable';
import { PropertyMultiSelectSection } from 'src/pages/Reporting/components/PropertyMultiSelectSection/PropertyMultiSelectSection';
import { ReportDateRange } from 'src/pages/Reporting/components/ReportDateRange/ReportDateRange';
import { DownloadReportButton } from 'src/pages/Reporting/components/DownloadReportButton/DownloadReportButton';
import TableSkeleton from 'src/components/TableSkeleton/TableSkeleton';
import {
  customerFeedBackHeaders,
  occupancyDeficiencyHeaders,
  propertyAggregateHeaders,
  ReportTypeOption,
  types,
} from 'src/pages/Reporting/constants';
import './Reporting.scss';

const ReportingForm = () => {
  const {
    formState: state,
    selectedProperties,
    selectedReportTypeOption,
    updateSelectedValues,
    updateFromDate,
    updateToDate,
    updateSelectedProperties,

    properties,
    propertyOptions,
    isLoadingProperties,
    propertiesError,

    reports,
    feedbackReport,
    isGeneratingReport,

    occupancyReportData,
    propertyAggregateReportData,
    isLoadingOccupancyReport,

    reportHeaders,
    fileName,

    generateReport,
    generateOccupancyReport,
    changeReportType,
    clearResults,
    isGenerateReportDisabled,
    showClearResultsButton,

    paginatedTableData,
    currentPage,
    totalPages,
    showPaginationControls,
    setCurrentPage,
  }: IUseReportingPageOrchestratorReturn = useReportingPageOrchestrator();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Row style={{ flex: '1 1 auto' }}>
        <Col xs="12" md="12">
          <Col xs="12" md="12">
            <label className="input__label reporting__select-label">
              Select Reporting Type
            </label>
            <ModernSelect
              size="regular"
              value={selectedReportTypeOption}
              onChange={(option: ReportTypeOption | null) => {
                if (option) {
                  changeReportType(option.value);
                }
              }}
              options={types}
              placeholder="Select Reporting Type"
              isSearchable={false}
              styles={{
                menu: (base: any) => ({ ...base, zIndex: 5 }),
              }}
            />
            {state.type === 'Service Request Report' ? (
              <ServiceRequestStatsForm properties={properties} />
            ) : state.type === 'OccupancyDeficiencyReport' ? (
              <OccupancyDeficiencyForm
                properties={properties}
                onGenerateReport={generateOccupancyReport}
                selectedProperties={selectedProperties}
                updateSelectedProperties={updateSelectedProperties}
              />
            ) : (
              <>
                <PropertyMultiSelectSection
                  propertyOptions={propertyOptions}
                  selectedValues={state.selectedValues}
                  showRequiredMarker={state.type !== 'User Status Report'}
                  isLoadingProperties={isLoadingProperties}
                  propertiesError={propertiesError}
                  onSelectedValuesChange={updateSelectedValues}
                />
                {state.type !== 'User Status Report' && (
                  <ReportDateRange
                    fromDate={state.fromDate}
                    toDate={state.toDate}
                    onFromDateChange={updateFromDate}
                    onToDateChange={updateToDate}
                  />
                )}
                <div style={{ marginBottom: 20 }} />
                <div className="reporting__form-actions">
                  <Button
                    color="teal"
                    size="small"
                    label="Generate Report"
                    disabled={isGenerateReportDisabled}
                    onClick={generateReport}
                  />
                  {showClearResultsButton && (
                    <Button
                      color="white"
                      size="small"
                      label="Clear Results"
                      onClick={clearResults}
                    />
                  )}
                </div>
              </>
            )}
          </Col>
        </Col>
      </Row>
      <Row style={{ marginTop: '3rem' }}>
        <Col xs="12" md="12">
          <div
            style={{
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginRight: 20,
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {state.type === 'OccupancyDeficiencyReport' ? (
              <>
                <DownloadReportButton
                  data={propertyAggregateReportData}
                  headers={propertyAggregateHeaders}
                  filename="OccupancyDeficiencyReport_ByProject.csv"
                  title="Download Stats by Project Report"
                />
                <DownloadReportButton
                  data={occupancyReportData}
                  headers={occupancyDeficiencyHeaders}
                  filename="OccupancyDeficiencyReport_ByUnit.csv"
                  title="Download Stats by Units Report"
                />
              </>
            ) : state.type === 'Customer Feedback Report' ? (
              <DownloadReportButton
                data={feedbackReport}
                headers={customerFeedBackHeaders}
                filename="CustomerFeedbackReport.csv"
                title="Download Customer Feedback Data"
              />
            ) : (
              <DownloadReportButton
                data={reports}
                headers={reportHeaders}
                filename={fileName}
                title={`Download ${state.type} Data`}
              />
            )}
            {state.type === 'Service Request Report' && (
              <ServiceRequestStatsReportSidebar />
            )}
          </div>
        </Col>
        {/* Bottom Tables */}
        <Col xs="12" md="12">
          <div className="reporting-results__tables">
            {state.type === 'OccupancyDeficiencyReport' && (
              <>
                <PropertyAggregateTable
                  data={propertyAggregateReportData}
                  isLoading={isLoadingOccupancyReport}
                />
                <OccupancyDeficiencyTable
                  reports={occupancyReportData}
                  isLoading={isLoadingOccupancyReport}
                />
              </>
            )}
            {isGeneratingReport ? (
              <TableSkeleton />
            ) : (
              <>
                {state.type === 'Reporting' && (
                  <ReportingTable
                    reports={paginatedTableData}
                    from="Property"
                  />
                )}
                {state.type === 'User Activity' && (
                  <ReportingUserActivityTable
                    reports={paginatedTableData}
                    from="Property"
                  />
                )}
                {state.type === 'User Status Report' && (
                  <UserStatusReports reports={paginatedTableData} />
                )}
                {state.type === 'Customer Feedback Report' && (
                  <CustomerFeedbackReportTable reports={paginatedTableData} />
                )}
              </>
            )}
            {state.type === 'Service Request Report' && (
              <ServiceRequestStatsReport />
            )}
          </div>
          {showPaginationControls && (
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ReportingForm;
